import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import Chart from "react-apexcharts"
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../states/containers/store'
import { ResponseMonthlyMemberReport, ResponseMonthlyTeamReport } from '../../../../../states/types/report'
import { format, secToHour, secToMinute } from '../../../../../utils/date'
import MonthlyHeaderBarGraph from './MonthlyHeaderBarGraph'
import Loading from '../../../common/Loading'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'
import Tips from '../../../common/Tips'

interface Heatmap {
  type: string
  range: string
  target: string
  id: number
  title: string
  legend: string
  startColor: string
  endColor: string
  minimum: number
  displaySearchLink: boolean
  name: string
}

type ScoreColumnName = 'process_score' | 'resource_score' | 'improvement_score' | 'legacy_score' | 'stress_score'

interface Scores {
  label: ScoreColumnName
  current: number
  past: number | null
  company: number
  qasee?: number
  team?: number
  occupation?: number
  distributions: {
    from: number
    to: number
    percent: number
  }[]
}

interface Props {
  target: string
  heatmapModalOpenCallback: (heatmap: Heatmap) => void,
}

const MonthlyHeader: FC<Props> = (props) => {
  const {
    target,
    heatmapModalOpenCallback,
  } = props

  const {
    teams,
  } = useSelector((state: RootState) => (state.commonPart))

  const {
    report,
  } = useSelector((state: RootState) => (state))

  const { t } = useTranslation()

  const contentsRef = useRef<HTMLDivElement>(null)
  const graphRef = useRef<HTMLDivElement>(null)

  const [total, setTotal] = useState<number>(0)
  const [data, setData] = useState<ResponseMonthlyTeamReport | ResponseMonthlyMemberReport>()
  const [icon, setIcon] = useState<string>()

  const [graphWidth, setGraphWidth] = useState<number>(0)

  const buildTitle = useMemo(() => {
    if (data && "member" in data && data.member) {
      if (data.member.image_url) {
        setIcon(data.member.image_url)
      } else {
        setIcon(`/static/images/profile/${(data.member.id || 0) % 12}.jpeg`)
      }
      return `${data.member.name} さん`
    }
    if (data && "team" in data && data.team) {
      return `${data.team.name} チーム`
    }
    return ""
  }, [data])

  useEffect(() => {
    const onResize = () => {
      let num = 0
      if (((graphRef.current?.clientWidth || 0) / 2) > 350) {
        num = 350
      } else {
        num = (graphRef.current?.clientWidth || 0) / 2
      }
      setGraphWidth(num)
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  useEffect(() => {
    if (!target) return
    if (report.isLoading) return
    if (target === "team") {
      if (!report.monthlyTeamReport) return
      setData(report.monthlyTeamReport)
    } else {
      if (!report.monthlyMemberReport) return
      setData(report.monthlyMemberReport)
    }
  }, [target, report.isLoading])

  useEffect(() => {
    if (!data || !data.scores) return
    let sum = 0
    data.scores.forEach((score) => {
      sum += score.current
    })
    setTotal(
      sum / 5,
    )
    const width = ((graphRef.current?.clientWidth || 0) / 2) > 350 ? 350 : (graphRef.current?.clientWidth || 0) / 2
    setGraphWidth(width)
  }, [data])

  return (
    <>
      {
        report.isLoading ? (
          <>
            <Loading />
          </>
        ) : (
          <section id="monthly_header" className="page_contents" ref={contentsRef}>
            <div className="content-block monthly">
              <div className="block-detail header_area">
                <div className="title no_trans">
                  {buildTitle}
                </div>
                <div className="description">
                  {
                    data?.is_past_data_exist ? (
                      <>
                        {t('report.monthly.total_comment.prefix')}
                        {t(`report.monthly.total_comment.main.degree.${data?.summary?.comment.total_comment_factors.degree}`)}
                        {t(`report.monthly.total_comment.main.comparison_evaluation.${
                          data?.summary?.comment.total_comment_factors.comparison_evaluation
                        }`)}
                        {t(`report.monthly.total_comment.main.work_style.${data?.summary?.comment.total_comment_factors.work_style}`)}
                      </>
                    ) : (
                      <>
                        {t(`report.monthly.total_comment.no_previous_data_comment.${
                          data?.summary?.no_previous_report_comment?.max_score_name}`)}
                      </>
                    )
                  }
                </div>
                <div className="graph_contents">
                  <div className="left_area">
                    <div className="graph_area" ref={graphRef}>
                      {
                        graphWidth && (
                          <div
                            className="graph_wrap"
                            style={{
                              height: graphWidth,
                            }}
                          >
                            <div
                              className="graph"
                              style={{
                                width: graphWidth - 40,
                                height: graphWidth - 40,
                                backgroundImage:
                                  `radial-gradient(#FFFFFF 60%, transparent 60%),
                                  conic-gradient(#4372BA 0% ${total}%, #EAEBEB ${total}%, #FFFFFF 100%)`,
                              }}
                            >
                              <div className="detail">
                                {
                                  target === "team" && data && "team" in data ? (
                                    <>
                                      <div className="team_icon no_trans">
                                        {teams?.find((team) => team.id === data?.team?.id)?.name.slice(0, 1)}
                                      </div>
                                    </>
                                  ) : (
                                    <div className="member_icon">
                                      <img alt="" src={icon} />
                                    </div>
                                  )
                                }
                                <div className="date">
                                  {format(data?.date?.from, "YYYY/MM")}
                                </div>
                                <div className="score">
                                  <div>
                                    {data?.summary?.evaluation.total_evaluation}
                                  </div>
                                  {
                                    data?.summary?.evaluation.total_evaluation === "Excellent" && (
                                      <img
                                        className="excellent_ribbon"
                                        src="/static/images/bg/excellent_ribbon.png"
                                        alt=""
                                      />
                                    )
                                  }
                                </div>
                                <ReactSVG
                                  src={`/static/images/icon/icon-${data?.summary?.evaluation.comparison_evaluation}.svg`}
                                  className={`icon ${data?.summary?.evaluation.comparison_evaluation}`}
                                />
                                <div
                                  style={{
                                    marginTop: "4px",
                                  }}
                                >
                                  先月比
                                </div>
                              </div>
                            </div>
                            <div
                              className="guard"
                              style={{
                                width: graphWidth - 114,
                                height: graphWidth - 114,
                              }}
                            />
                          </div>
                        )
                      }
                      <div className="radar_area">
                        <div className="radar_title_area">
                          <hr />
                          <div className="radar_title">
                            バランス
                          </div>
                        </div>
                        <div className="radar">
                          <Chart
                            type="radar"
                            height={360}
                            options={{
                              chart: {
                                toolbar: {
                                  show: false,
                                },
                              },
                              dataLabels: {
                                enabled: false,
                              },
                              labels: ['セルフマネジメント', 'コンディション', 'リソース', 'プロセス', 'デジタル化'],
                              plotOptions: {
                                radar: {
                                  polygons: {
                                    strokeColors: '#e8e8e8',
                                    strokeWidth: '1',
                                    connectorColors: '#e8e8e8',
                                  },
                                },
                              },
                              colors: ["transparent", "#CCCCCC", "#008FFB"],
                              fill: {
                                opacity: [0, 0.5, 0.5],
                              },
                              markers: {
                                size: 0,
                              },
                              yaxis: {
                                tickAmount: 6,
                              },
                              legend: {
                                show: false,
                              },
                            }}
                            series={[
                              {
                                name: "",
                                data: [100, 100, 100, 100, 100],
                              },
                              {
                                name: "先月",
                                data: [
                                  data?.scores?.find((score) => score.label === "improvement_score")?.past,
                                  data?.scores?.find((score) => score.label === "stress_score")?.past,
                                  data?.scores?.find((score) => score.label === "resource_score")?.past,
                                  data?.scores?.find((score) => score.label === "process_score")?.past,
                                  data?.scores?.find((score) => score.label === "legacy_score")?.past,
                                ],
                              },
                              {
                                name: "今月",
                                data: [
                                  data?.scores?.find((score) => score.label === "improvement_score")?.current,
                                  data?.scores?.find((score) => score.label === "stress_score")?.current,
                                  data?.scores?.find((score) => score.label === "resource_score")?.current,
                                  data?.scores?.find((score) => score.label === "process_score")?.current,
                                  data?.scores?.find((score) => score.label === "legacy_score")?.current,
                                ],
                              },
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="comment_area">
                      <div className="comment_block">
                        <div className="comment_title_area">
                          <hr />
                          <div className="comment_title">
                            傾向
                          </div>
                        </div>
                        <div className="comment_detail">
                          {
                            data?.is_past_data_exist ? (
                              <>
                                <p>
                                  {t('report.monthly.tendency_comment.largest_variation_comment.prefix', {
                                    largest_variation_indicator_name:
                                      t(`report.indicator.${data?.summary?.comment.tendency_comment_factors?.largest_variation_indicator_name}`),
                                  })}
                                  {t(`report.monthly.tendency_comment.largest_variation_comment.score_status.${
                                    data?.summary?.comment.tendency_comment_factors?.score_status
                                  }`)}
                                  {t('report.monthly.tendency_comment.largest_variation_comment.suffix')}
                                </p>
                                <p>
                                  {t('report.monthly.tendency_comment.max_score_comment', {
                                    max_score_name:
                                      t(`report.monthly.score.${data?.summary?.comment.tendency_comment_factors?.max_score_name}`),
                                  })}
                                  {t('report.monthly.tendency_comment.previous_worst_score_comment.prefix', {
                                    previous_worst_score_name:
                                      t(`report.monthly.score.${data?.summary?.comment.tendency_comment_factors?.previous_worst_score_name}`),
                                  })}
                                  {t(`report.monthly.tendency_comment.previous_worst_score_comment.improve_status.${
                                    data?.summary?.comment.tendency_comment_factors?.improve_status
                                  }`)}
                                  {t('report.monthly.tendency_comment.previous_worst_score_comment.suffix')}
                                  {t(`report.monthly.tendency_comment.promotion_comment.promotion_type.${
                                    data?.summary?.comment.tendency_comment_factors?.promotion_type
                                  }`)}
                                </p>
                              </>
                            ) : (
                              <>
                                {
                                  data?.summary?.no_previous_report_comment?.max_score_name
                                  === data?.summary?.no_previous_report_comment?.min_score_name ? (
                                    <>
                                      {
                                        t('report.monthly.tendency_comment.no_previous_data_comment.main_flat')
                                      }
                                    </>
                                  ) : (
                                    <>
                                      {
                                        t('report.monthly.tendency_comment.no_previous_data_comment.main', {
                                          max_score_detail: t(`report.monthly.tendency_comment.no_previous_data_comment.${
                                            data?.summary?.no_previous_report_comment?.max_score_name}`),
                                          min_score_detail: t(`report.monthly.tendency_comment.no_previous_data_comment.${
                                            data?.summary?.no_previous_report_comment?.min_score_name}`),
                                        })
                                      }
                                    </>
                                  )
                                }
                                {
                                  t(`report.monthly.tendency_comment.no_previous_data_comment.suffix.${target}`)
                                }
                              </>
                            )
                          }
                        </div>
                      </div>
                      <div className="comment_block">
                        <div className="comment_title_area">
                          <hr />
                          <div className="comment_title">
                            チェックポイント
                          </div>
                        </div>
                        <div className="comment_detail">
                          {
                            data?.is_past_data_exist && (
                              <>
                                <p>
                                  {
                                    data?.summary?.comment.high_check_comment_factors?.is_all_score_decreased ? (
                                      <>
                                        {t('report.monthly.high_check_comment.improved_score_comment.improve_status.all_decreased', {
                                          max_increased_indicator_name:
                                            t(`report.indicator.${
                                              data?.summary?.comment.high_check_comment_factors?.max_increased_indicator_name}`),
                                        })}
                                      </>
                                    ) : (
                                      <>
                                        {t('report.monthly.high_check_comment.improved_score_comment.improve_status.increased', {
                                          max_increased_score_name:
                                            t(`report.monthly.score.${
                                                data?.summary?.comment.high_check_comment_factors?.max_increased_score_name}`),
                                          primary_factor_indicator_name:
                                            t(`report.indicator.${
                                                data?.summary?.comment.high_check_comment_factors?.primary_factor_indicator_name}`),
                                        })}
                                      </>
                                    )
                                  }
                                </p>
                              </>
                            )
                          }
                          {
                            data?.summary?.check_comment && (
                              <>
                                {
                                  t('report.monthly.check_comment', {
                                    score_name: t(`report.monthly.score.${data?.summary?.check_comment.score_name}`),
                                    indicator_name: t(`report.indicator.${data?.summary?.check_comment.indicator_name}`) })
                                }
                              </>
                            )
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="right_area">
                    <div className="block">
                      <div className="header_text_area">
                        <hr />
                        <div className="header_text">
                          パフォーマンス【効率度】
                        </div>
                      </div>
                      <div className="group">
                        <MonthlyHeaderBarGraph
                          title="resource"
                          value={data?.scores?.find((score) => score.label === "resource_score")?.current || 0}
                          checked={data?.scores?.find((score) => score.label === "resource_score")?.checked || false}
                        />
                        <MonthlyHeaderBarGraph
                          title="legacy"
                          value={data?.scores?.find((score) => score.label === "legacy_score")?.current || 0}
                          checked={data?.scores?.find((score) => score.label === "legacy_score")?.checked || false}
                        />
                        <MonthlyHeaderBarGraph
                          title="process"
                          value={data?.scores?.find((score) => score.label === "process_score")?.current || 0}
                          checked={data?.scores?.find((score) => score.label === "process_score")?.checked || false}
                        />
                      </div>
                    </div>
                    <div className="block">
                      <div className="header_text_area">
                        <hr />
                        <div className="header_text">
                          エンゲージメント【活動度】
                        </div>
                      </div>
                      <div className="group">
                        <MonthlyHeaderBarGraph
                          title="improvement"
                          value={data?.scores?.find((score) => score.label === "improvement_score")?.current || 0}
                          checked={data?.scores?.find((score) => score.label === "improvement_score")?.checked || false}
                        />
                        <MonthlyHeaderBarGraph
                          title="stress"
                          value={data?.scores?.find((score) => score.label === "stress_score")?.current || 0}
                          checked={data?.scores?.find((score) => score.label === "stress_score")?.checked || false}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="down_area">
                  <div className={`detail_block ${data?.summary?.average.work_time.checked && 'checked'}`}>
                    {
                      data?.summary?.average.work_time.checked && (
                        <ReactSVG
                          src="/static/images/icon/icon-warn.svg"
                          className="icon_warn"
                        />
                      )
                    }
                    <div className="detail_wrap">
                      <div className="detail_text_area">
                        <Tips
                          description={t(`report.indicator_help.monthly.${target}.summary.average_work_time`)}
                          className="detail_title"
                        >
                          <>
                            平均勤務時間
                          </>
                        </Tips>
                        <div className="detail_value">
                          {
                            secToHour(data?.summary?.average.work_time.value || 0) && (
                              <>
                                <span>
                                  {secToHour(data?.summary?.average.work_time.value || 0)}
                                </span>
                                時間
                              </>
                            )
                          }
                          {
                            secToMinute(data?.summary?.average.work_time.value || 0) && (
                              <>
                                <span>
                                  {secToMinute(data?.summary?.average.work_time.value || 0)}
                                </span>
                                分
                              </>
                            )
                          }
                          <div className="icon_area">
                            {
                              data?.is_past_data_exist
                              && Math.abs(data?.summary?.average.work_time.increased_value || 0) >= 60
                              && (data?.summary?.average.work_time.increased_value || 0) > 0
                              && (
                                <ReactSVG
                                  src="/static/images/icon/icon-up.svg"
                                  className="icon up"
                                />
                              )
                            }
                            {
                              data?.is_past_data_exist
                              && Math.abs(data?.summary?.average.work_time.increased_value || 0) >= 60
                              && (data?.summary?.average.work_time.increased_value || 0) < 0
                              && (
                                <ReactSVG
                                  src="/static/images/icon/icon-down.svg"
                                  className="icon down"
                                />
                              )
                            }
                          </div>
                        </div>
                      </div>
                      <button
                        data-gtm-click="平均勤務時間 - header_area"
                        type="button"
                        onClick={
                          () => heatmapModalOpenCallback({
                            type: "hour",
                            range: "monthly",
                            target: target,
                            id: 1,
                            title: "work_time",
                            legend: "勤務時間(h)",
                            startColor: "#ECF0F5",
                            endColor: "#008FFB",
                            minimum: 5000,
                            displaySearchLink: true,
                            name: "",
                          })
                        }
                      >
                        比較を見る
                      </button>
                    </div>
                    <div className="detail_description">
                      {
                        data?.is_past_data_exist && (
                          <>
                            {
                              Math.abs(data?.summary?.average.work_time.increased_value || 0) < 60 ? (
                                <>
                                  前月と比べて変化はありません
                                </>
                              ) : (
                                <>
                                  前月よりも
                                  {
                                    secToHour(Math.abs(data?.summary?.average.work_time.increased_value || 0)) ? (
                                      <>
                                        {secToHour(Math.abs(data?.summary?.average.work_time.increased_value || 0))}
                                        時間
                                      </>
                                    ) : (
                                      <></>
                                    )
                                  }
                                  {Number(secToMinute(Math.abs(data?.summary?.average.work_time.increased_value || 0)))}
                                  分
                                  {
                                    (data?.summary?.average.work_time.increased_value || 0) > 0 ? (
                                      <>
                                        増加
                                      </>
                                    ) : (
                                      <>
                                        減少
                                      </>
                                    )
                                  }
                                  しています
                                </>
                              )
                            }
                          </>
                        )
                      }
                    </div>
                  </div>
                  <div className={`detail_block ${data?.summary?.average.active_time.checked && 'checked'}`}>
                    {
                      data?.summary?.average.active_time.checked && (
                        <ReactSVG
                          src="/static/images/icon/icon-warn.svg"
                          className="icon_warn"
                        />
                      )
                    }
                    <div className="detail_wrap">
                      <div className="detail_text_area">
                        <Tips
                          description={t(`report.indicator_help.monthly.${target}.summary.average_active_time`)}
                          className="detail_title"
                        >
                          <>
                            平均作業時間
                          </>
                        </Tips>
                        <div className="detail_value">
                          {
                            secToHour(data?.summary?.average.active_time.value || 0) && (
                              <>
                                <span>
                                  {secToHour(data?.summary?.average.active_time.value || 0)}
                                </span>
                                時間
                              </>
                            )
                          }
                          {
                            secToMinute(data?.summary?.average.active_time.value || 0) && (
                              <>
                                <span>
                                  {secToMinute(data?.summary?.average.active_time.value || 0)}
                                </span>
                                分
                              </>
                            )
                          }
                          <div className="icon_area">
                            {
                              data?.is_past_data_exist
                              && Math.abs(data?.summary?.average.active_time.increased_value || 0) >= 60
                              && (data?.summary?.average.active_time.increased_value || 0) > 0
                              && (
                                <ReactSVG
                                  src="/static/images/icon/icon-up.svg"
                                  className="icon up"
                                />
                              )
                            }
                            {
                              data?.is_past_data_exist
                              && Math.abs(data?.summary?.average.active_time.increased_value || 0) >= 60
                              && (data?.summary?.average.active_time.increased_value || 0) < 0
                              && (
                                <ReactSVG
                                  src="/static/images/icon/icon-down.svg"
                                  className="icon down"
                                />
                              )
                            }
                          </div>
                        </div>
                      </div>
                      <button
                        data-gtm-click="平均作業時間 - header_area"
                        type="button"
                        onClick={
                          () => heatmapModalOpenCallback({
                            type: "hour",
                            range: "monthly",
                            target: target,
                            id: 1,
                            title: "active_time",
                            legend: "作業時間(h)",
                            startColor: "#ECF0F5",
                            endColor: "#008FFB",
                            minimum: 0,
                            displaySearchLink: true,
                            name: "",
                          })
                        }
                      >
                        比較を見る
                      </button>
                    </div>
                    <div className="detail_description">
                      {
                        data?.is_past_data_exist && (
                          <>
                            {
                              Math.abs(data?.summary?.average.active_time.increased_value || 0) < 60 ? (
                                <>
                                  前月と比べて変化はありません
                                </>
                              ) : (
                                <>
                                  前月よりも
                                  {
                                    secToHour(Math.abs(data?.summary?.average.active_time.increased_value || 0)) ? (
                                      <>
                                        {secToHour(Math.abs(data?.summary?.average.active_time.increased_value || 0))}
                                        時間
                                      </>
                                    ) : (
                                      <></>
                                    )
                                  }
                                  {Number(secToMinute(Math.abs(data?.summary?.average.active_time.increased_value || 0)))}
                                  分
                                  {
                                    (data?.summary?.average.active_time.increased_value || 0) > 0 ? (
                                      <>
                                        増加
                                      </>
                                    ) : (
                                      <>
                                        減少
                                      </>
                                    )
                                  }
                                  しています
                                </>
                              )
                            }
                          </>
                        )
                      }
                    </div>
                  </div>
                  <div className={`detail_block ${data?.summary?.average.biz_point.checked && 'checked'}`}>
                    {
                      data?.summary?.average.biz_point.checked && (
                        <ReactSVG
                          src="/static/images/icon/icon-warn.svg"
                          className="icon_warn"
                        />
                      )
                    }
                    <div className="detail_wrap">
                      <div className="detail_text_area">
                        <Tips
                          description={t(`report.indicator_help.monthly.${target}.summary.average_biz_point`)}
                          className="detail_title"
                        >
                          <>
                            平均操作量
                          </>
                        </Tips>
                        <div className="detail_value">
                          <span>
                            {Math.floor(data?.summary?.average.biz_point.value || 0).toLocaleString()}
                          </span>
                          biz
                          <div className="icon_area">
                            {
                              data?.is_past_data_exist
                              && Math.abs(data?.summary?.average.biz_point.increased_value || 0) >= 500
                              && (data?.summary?.average.biz_point.increased_value || 0) > 0
                              && (
                                <ReactSVG
                                  src="/static/images/icon/icon-up.svg"
                                  className="icon up"
                                />
                              )
                            }
                            {
                              data?.is_past_data_exist
                              && Math.abs(data?.summary?.average.biz_point.increased_value || 0) >= 500
                              && (data?.summary?.average.biz_point.increased_value || 0) < 0
                              && (
                                <ReactSVG
                                  src="/static/images/icon/icon-down.svg"
                                  className="icon down"
                                />
                              )
                            }
                          </div>
                        </div>
                      </div>
                      <button
                        data-gtm-click="平均操作量 - header_area"
                        type="button"
                        onClick={
                          () => heatmapModalOpenCallback({
                            type: "biz",
                            range: "monthly",
                            target: target,
                            id: 1,
                            title: "biz_point",
                            legend: "操作量(biz)",
                            startColor: "#ECF0F5",
                            endColor: "#008FFB",
                            minimum: 0,
                            displaySearchLink: true,
                            name: "",
                          })
                        }
                      >
                        比較を見る
                      </button>
                    </div>
                    <div className="detail_description">
                      {
                        data?.is_past_data_exist && (
                          <>
                            {
                              Math.abs(data?.summary?.average.biz_point.increased_value || 0) < 500 ? (
                                <>
                                  前月と比べて変化はありません
                                </>
                              ) : (
                                <>
                                  前月よりも
                                  {Math.round(Math.abs(data?.summary?.average.biz_point.increased_value || 0)).toLocaleString()}
                                  Biz
                                  {
                                    (data?.summary?.average.biz_point.increased_value || 0) > 0 ? (
                                      <>
                                        増加
                                      </>
                                    ) : (
                                      <>
                                        減少
                                      </>
                                    )
                                  }
                                  しています
                                </>
                              )
                            }
                          </>
                        )
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )
      }
    </>
  )
}

export default MonthlyHeader
